import React, {useState} from 'react';
import {Box, IconButton, Tooltip} from '@mui/material';
import {GridColDef, GridRenderCellParams} from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import ShieldIcon from '@mui/icons-material/Shield';
import {useTranslation} from 'react-i18next';
import {useDataGridStyles} from '@/styles/dataGridStyles';
import {useSignals} from "@preact/signals-react/runtime";
import {store} from '@/service/store';
import {formatCurrency, formatDateTime} from '@/utils/formatters';
import TseDetailsDialog from './TseDetailsDialog';
import {ShiftData} from "@/components/shifts";
import {ExtendedShiftData} from "@/components/shifts/ShiftItem.tsx";
import AppDataGrid from "@/components/common/AppDataGrid.tsx";

interface ShiftsDataGridProps {
  onViewDetails: (shift: ShiftData) => void;
}

const ShiftsDataGrid: React.FC<ShiftsDataGridProps> = ({ onViewDetails }) => {
  useSignals();
  const [selectedShift, setSelectedShift] = useState<ExtendedShiftData | null>(null);
  const [tseDialogOpen, setTseDialogOpen] = useState(false);

  const { t } = useTranslation();
  const styles = useDataGridStyles();

  const handleTseDetailsClick = (shift: ExtendedShiftData) => {
    setSelectedShift(shift);
    setTseDialogOpen(true);
  };

  const handleCloseTseDialog = () => {
    setTseDialogOpen(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'customerNumber',
      headerName: t('events.customerNumber'),
      flex: 1,
      minWidth: 70,
    },
    {
      field: 'customerBusinessName',
      headerName: t('events.customerBusinessName'),
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'deviceType',
      headerName: t('events.deviceType'),
      flex: 1,
      minWidth: 130,
    },
    {
      field: 'deviceManufacturerNumber',
      headerName: t('events.deviceSerial'),
      flex: 1,
      minWidth: 90,
    },
    {
      field: 'startAt',
      headerName: t('events.start'),
      flex: 1,
      minWidth: 140,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.startAt),
      renderCell: ({ row }) => formatDateTime(row.startAt),
    },
    {
      field: 'endAt',
      headerName: t('events.end'),
      flex: 1,
      minWidth: 140,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.endAt),
      renderCell: ({ row }) => formatDateTime(row.endAt),
    },
    {
      field: 'totalAmount',
      headerName: t('events.totalAmount'),
      flex: 1,
      minWidth: 80,
      type: "number",
      renderCell: ({ row }) => formatCurrency(row.totalAmount),
    },
    {
      field: 'actions',
      headerName: t('events.actions'),
      sortable: false,
      width: 100, // Increased width to accommodate both buttons
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Tooltip title={t('events.viewDetails')}>
            <IconButton
              onClick={() => onViewDetails(params.row)}
              size="small"
              sx={{
                color: 'primary.main',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('events.viewTseDetails')}>
            <IconButton
              onClick={() => handleTseDetailsClick(params.row)}
              size="small"
              sx={{
                color: 'secondary.main',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ShieldIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{
      flex: 1,
      display: "flex",
      width: '100%',
    }}>
      <AppDataGrid
        rows={store.technician.recentInstallations.getShifts()}
        columns={columns}
        disableRowSelectionOnClick
        title={t('pages.dashboard.latestShifts')}
        initialState={{
          sorting: {
            sortModel: [{ field: 'endAt', sort: 'desc' }],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
      />
      {selectedShift && (
        <TseDetailsDialog
          installationUuid={selectedShift.installationUuid}
          open={tseDialogOpen}
          onClose={handleCloseTseDialog}
        />
      )}
    </Box>
  );
};

export default ShiftsDataGrid;
